import { useCallback, useEffect, useRef, useState } from "react";

const DEFAULT_MAX_DURATION_MS = 120_000;

type RecorderError =
  | "permission-denied"
  | "no-device"
  | "unsupported"
  | "unknown";

interface StartOptions {
  maxDurationMs?: number;
  onTimeout?: () => void;
  /**
   * Formats to try before the defaults (first supported wins). Chat asks for
   * MP4/AAC so its voice notes also play on iOS, which has no WebM/Opus.
   */
  preferMimeTypes?: string[];
}

export interface UseVoiceRecorderResult {
  isRecording: boolean;
  start: (options?: StartOptions) => Promise<void>;
  stop: () => Promise<Blob>;
  cancel: () => void;
  error: RecorderError | null;
  stream: MediaStream | null;
}

const MIME_CANDIDATES = [
  "audio/webm;codecs=opus",
  "audio/webm",
  "audio/mp4",
  "audio/ogg;codecs=opus",
  "audio/ogg",
];

const pickMimeType = (prefer: string[] = []): string | undefined => {
  if (typeof MediaRecorder === "undefined") return undefined;
  return [...prefer, ...MIME_CANDIDATES].find((t) => MediaRecorder.isTypeSupported(t));
};

export const useVoiceRecorder = (): UseVoiceRecorderResult => {
  const [isRecording, setIsRecording] = useState(false);
  const [error, setError] = useState<RecorderError | null>(null);
  const [stream, setStream] = useState<MediaStream | null>(null);

  const recorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const stopResolverRef = useRef<((value: Blob) => void) | null>(null);
  const stopRejecterRef = useRef<((reason?: unknown) => void) | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const teardown = useCallback(() => {
    if (timeoutRef.current !== null) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    recorderRef.current = null;
    chunksRef.current = [];
    stopResolverRef.current = null;
    stopRejecterRef.current = null;
    setIsRecording(false);
    setStream(null);
  }, []);

  const start = useCallback(async (options?: StartOptions) => {
    const {
      maxDurationMs = DEFAULT_MAX_DURATION_MS,
      onTimeout,
      preferMimeTypes,
    } = options ?? {};
    setError(null);
    if (typeof navigator === "undefined" || !navigator.mediaDevices?.getUserMedia) {
      setError("unsupported");
      throw new Error("MediaDevices not supported");
    }
    const mimeType = pickMimeType(preferMimeTypes);
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = mediaStream;
      setStream(mediaStream);
      const recorder = new MediaRecorder(mediaStream, mimeType ? { mimeType } : undefined);
      recorderRef.current = recorder;
      chunksRef.current = [];

      recorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
      };
      recorder.onstop = async () => {
        const resolve = stopResolverRef.current;
        const reject = stopRejecterRef.current;
        try {
          const blob = new Blob(chunksRef.current, {
            type: mimeType ?? "audio/webm",
          });
          teardown();
          resolve?.(blob);
        } catch (err) {
          teardown();
          reject?.(err);
        }
      };

      recorder.start();
      setIsRecording(true);
      timeoutRef.current = setTimeout(() => {
        const rec = recorderRef.current;
        if (rec && rec.state !== "inactive") {
          rec.onstop = null;
          try { rec.stop(); } catch { /* ignore */ }
        }
        teardown();
        onTimeout?.();
      }, maxDurationMs);
    } catch (err: unknown) {
      teardown();
      const name = (err as { name?: string })?.name;
      if (name === "NotAllowedError" || name === "SecurityError") setError("permission-denied");
      else if (name === "NotFoundError" || name === "OverconstrainedError") setError("no-device");
      else setError("unknown");
      throw err;
    }
  }, [teardown]);

  const stop = useCallback(() => {
    return new Promise<Blob>((resolve, reject) => {
      const recorder = recorderRef.current;
      if (!recorder || recorder.state === "inactive") {
        reject(new Error("Recorder is not active"));
        return;
      }
      stopResolverRef.current = resolve;
      stopRejecterRef.current = reject;
      recorder.stop();
    });
  }, []);

  const cancel = useCallback(() => {
    const recorder = recorderRef.current;
    if (recorder && recorder.state !== "inactive") {
      recorder.onstop = null;
      try {
        recorder.stop();
      } catch {
        // ignore
      }
    }
    teardown();
  }, [teardown]);

  useEffect(() => {
    return () => {
      teardown();
    };
  }, [teardown]);

  return { isRecording, start, stop, cancel, error, stream };
};
